import {
  Button,
  Checkbox,
  SelectInput,
  SelectInputItem,
  TabMenu,
  TabMenuContent,
  TabMenuList,
  TabMenuTrigger,
  TextField,
} from '@ads/components-react';
import { useTranslation } from 'react-i18next';
import { Textarea } from '@/shared/ui/atoms';

import { usePaymentPlanForm } from '../../../hooks/usePaymentPlanForm';
import type { PaymentMethod, ScholarshipStatus, ScholarshipType } from '../../../types/payment';

export interface PaymentPlanFormProps {
  readonly planId?: number | undefined;
  readonly onCancel: () => void;
  readonly onSaved: () => void;
}

/* ── Helper: label + textarea wrapper ───────────────────────────────────── */
function LabeledTextarea({
  id,
  label,
  placeholder,
  value,
  onChange,
  rows = 2,
}: {
  id: string;
  label: string;
  placeholder?: string;
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <div className="flex flex-col gap-quark">
      <label htmlFor={id} className="ts-caption-xxs text-ctx-content-base">
        {label}
      </label>
      <Textarea
        id={id}
        placeholder={placeholder ?? label}
        value={value}
        onChange={e => onChange(e.target.value)}
        rows={rows}
      />
    </div>
  );
}

/* ── Section header ─────────────────────────────────────────────────────── */
function SectionTitle({ children }: { children: string }) {
  return (
    <h3 className="border-b-hairline border-ctx-layout-border pb-xxs ts-subtitle-md text-ctx-content-title">
      {children}
    </h3>
  );
}

/* ── Calculated row ─────────────────────────────────────────────────────── */
function CalcField({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-quark">
      <span className="ts-caption-xxs text-ctx-content-placeholder">{label}</span>
      <span className="ts-paragraph-xs !font-medium text-ctx-content-title">{value}</span>
    </div>
  );
}

const fmtBrl = (v: number) => `R$ ${v.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;

/* ════════════════════════════════════════════════════════════════════════ */

export function PaymentPlanForm({ planId, onCancel, onSaved }: PaymentPlanFormProps) {
  const { t } = useTranslation('personManagement');
  const {
    form,
    updateField,
    monthlyValue,
    totalWithDiscount,
    totalWithSchoolDiscount,
    save,
    isEditing,
  } = usePaymentPlanForm(planId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    save();
    onSaved();
  };

  const dayOptions = Array.from({ length: 30 }, (_, i) => i + 1);
  const showScholarship = form.scholarshipStatus !== 'none';
  const showCompany = form.payAsCompany;
  const showThird = form.thirdPayment;

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-md">
      <TabMenu defaultValue="details">
        <TabMenuList>
          <TabMenuTrigger value="details">{t('planForm.tabs.details')}</TabMenuTrigger>
          <TabMenuTrigger value="values">{t('planForm.tabs.values')}</TabMenuTrigger>
        </TabMenuList>

        {/* ═══════════════ TAB DETALHES ═══════════════ */}
        <TabMenuContent value="details">
          <div className="flex flex-col gap-xxs pt-xs">
            {/* ── Informações gerais ── */}
            <SectionTitle>Informações Gerais</SectionTitle>
            <TextField
              fullWidth
              size="md"
              label={t('planForm.fields.description')}
              value={form.description}
              onChange={e => updateField('description', e.target.value)}
            />
            <LabeledTextarea
              id="plan-additional-information"
              label={t('planForm.fields.additionalInformation')}
              value={form.additionalInformation}
              onChange={v => updateField('additionalInformation', v)}
              rows={3}
            />
            <LabeledTextarea
              id="plan-observation"
              label={t('planForm.fields.observation')}
              value={form.observation}
              onChange={v => updateField('observation', v)}
            />
            <div className="flex flex-wrap gap-lg">
              <Checkbox
                label={t('planForm.fields.hidePaymentPlan')}
                checked={form.hidePaymentPlan}
                onCheckedChange={c => {
                  if (c !== 'indeterminate') {
                    updateField('hidePaymentPlan', c);
                  }
                }}
              />
              <Checkbox
                label={t('planForm.fields.automaticGenerateInvoice')}
                checked={form.automaticGenerateInvoice}
                onCheckedChange={c => {
                  if (c !== 'indeterminate') {
                    updateField('automaticGenerateInvoice', c);
                  }
                }}
              />
            </div>

            {/* ── Bolsa ── */}
            <SectionTitle>Bolsa</SectionTitle>
            <div className="grid grid-cols-1 gap-md md:grid-cols-3">
              <SelectInput
                fullWidth
                label={t('planForm.fields.scholarshipStatus')}
                value={form.scholarshipStatus}
                onValueChange={v => updateField('scholarshipStatus', v as ScholarshipStatus)}
              >
                <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                  {t('scholarshipStatus.none')}
                </SelectInputItem>
                <SelectInputItem value="partial" custom={{ root: 'text-nowrap' }}>
                  {t('scholarshipStatus.partial')}
                </SelectInputItem>
                <SelectInputItem value="full" custom={{ root: 'text-nowrap' }}>
                  {t('scholarshipStatus.full')}
                </SelectInputItem>
              </SelectInput>
              {showScholarship && (
                <SelectInput
                  label="Tipo de Bolsa"
                  fullWidth
                  value={form.scholarshipTypeId || 'none'}
                  onValueChange={v =>
                    updateField('scholarshipTypeId', (v === 'none' ? '' : v) as ScholarshipType)
                  }
                >
                  <SelectInputItem value="none" custom={{ root: 'text-nowrap' }}>
                    Selecione...
                  </SelectInputItem>
                  <SelectInputItem value="merit" custom={{ root: 'text-nowrap' }}>
                    Mérito
                  </SelectInputItem>
                  <SelectInputItem value="social" custom={{ root: 'text-nowrap' }}>
                    Social
                  </SelectInputItem>
                  <SelectInputItem value="employee" custom={{ root: 'text-nowrap' }}>
                    Funcionário
                  </SelectInputItem>
                  <SelectInputItem value="partner" custom={{ root: 'text-nowrap' }}>
                    Convênio
                  </SelectInputItem>
                  <SelectInputItem value="other" custom={{ root: 'text-nowrap' }}>
                    Outro
                  </SelectInputItem>
                </SelectInput>
              )}
              {showScholarship && (
                <TextField
                  fullWidth
                  label={t('planForm.fields.schoolDiscount')}
                  type="number"
                  size="md"
                  value={String(form.schoolDiscount)}
                  onChange={e => updateField('schoolDiscount', Number(e.target.value))}
                  disabled
                />
              )}
            </div>
            {showScholarship && (
              <LabeledTextarea
                id="plan-scholarship-reason"
                label={t('planForm.fields.scholarshipReason')}
                value={form.scholarshipReason}
                onChange={v => updateField('scholarshipReason', v)}
              />
            )}

            {/* ── Pagamento como empresa ── */}
            <SectionTitle>Pagamento como Empresa</SectionTitle>
            <Checkbox
              label="Pagar como Empresa"
              checked={form.payAsCompany}
              onCheckedChange={c => {
                if (c !== 'indeterminate') {
                  updateField('payAsCompany', c);
                }
              }}
            />
            {showCompany && (
              <div className="flex flex-col gap-xxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
                <div className="grid grid-cols-1 gap-md md:grid-cols-2">
                  <TextField
                    size="md"
                    fullWidth
                    label="Razão Social"
                    placeholder="Razão Social"
                    value={form.companyName}
                    onChange={e => updateField('companyName', e.target.value)}
                  />
                  <TextField
                    label="CNPJ"
                    size="md"
                    fullWidth
                    placeholder="00.000.000/0000-00"
                    value={form.cnpj}
                    onChange={e => updateField('cnpj', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-md md:grid-cols-2">
                  <div className="flex items-end gap-xs">
                    <div className="flex-1">
                      <TextField
                        size="md"
                        fullWidth
                        label="Inscrição Estadual"
                        placeholder="Inscrição Estadual"
                        value={form.ie}
                        onChange={e => updateField('ie', e.target.value)}
                        disabled={form.ieFree}
                      />
                    </div>
                    <Checkbox
                      label="Isento"
                      checked={form.ieFree}
                      onCheckedChange={c => {
                        if (c !== 'indeterminate') {
                          updateField('ieFree', c);
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-md md:grid-cols-3">
                  <TextField
                    size="md"
                    fullWidth
                    label="Nome do Contato"
                    placeholder="Nome do Contato"
                    value={form.companyContactName}
                    onChange={e => updateField('companyContactName', e.target.value)}
                  />
                  <TextField
                    size="md"
                    fullWidth
                    label="Telefone do Contato"
                    placeholder="Telefone do Contato"
                    value={form.companyContactPhone}
                    onChange={e => updateField('companyContactPhone', e.target.value)}
                  />
                  <TextField
                    size="md"
                    fullWidth
                    label="E-mail do Contato"
                    placeholder="E-mail do Contato"
                    type="email"
                    value={form.companyContactEmail}
                    onChange={e => updateField('companyContactEmail', e.target.value)}
                  />
                </div>
              </div>
            )}

            {/* ── Pagamento por terceiro ── */}
            <SectionTitle>Pagamento por Terceiro</SectionTitle>
            <Checkbox
              label="Pagamento por Terceiro"
              checked={form.thirdPayment}
              onCheckedChange={c => {
                if (c !== 'indeterminate') {
                  updateField('thirdPayment', c);
                }
              }}
            />
            {showThird && (
              <div className="flex flex-col gap-xxs rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
                <div className="grid grid-cols-1 gap-md md:grid-cols-2">
                  <TextField
                    size="md"
                    fullWidth
                    label="Nome do Terceiro"
                    placeholder="Nome do Terceiro"
                    value={form.thirdName}
                    onChange={e => updateField('thirdName', e.target.value)}
                  />
                  <TextField
                    size="md"
                    fullWidth
                    label="CPF/CNPJ do Terceiro"
                    placeholder="CPF/CNPJ do Terceiro"
                    value={form.thirdDocument}
                    onChange={e => updateField('thirdDocument', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-md md:grid-cols-2">
                  <TextField
                    size="md"
                    fullWidth
                    label="E-mail do Terceiro"
                    placeholder="E-mail do Terceiro"
                    type="email"
                    value={form.thirdEmail}
                    onChange={e => updateField('thirdEmail', e.target.value)}
                  />
                  <TextField
                    size="md"
                    fullWidth
                    label="Telefone do Terceiro"
                    placeholder="Telefone do Terceiro"
                    value={form.thirdPhone}
                    onChange={e => updateField('thirdPhone', e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </TabMenuContent>

        {/* ═══════════════ TAB VALORES ═══════════════ */}
        <TabMenuContent value="values">
          <div className="flex flex-col gap-xxs pt-xs">
            {/* ── Valor e método ── */}
            <SectionTitle>Valor e Método de Pagamento</SectionTitle>
            <div className="grid grid-cols-1 gap-md md:grid-cols-3">
              <TextField
                fullWidth
                size="md"
                label="Valor da Matrícula (R$)"
                placeholder="Valor da Matrícula (R$)"
                type="number"
                value={String(form.valueRegistration)}
                onChange={e => updateField('valueRegistration', Number(e.target.value))}
              />
              <SelectInput
                label={t('planForm.fields.paymentMethod')}
                value={form.paymentMethod}
                onValueChange={v => updateField('paymentMethod', v as PaymentMethod)}
              >
                <SelectInputItem value="boleto" custom={{ root: 'text-nowrap' }}>
                  {t('paymentMethod.boleto')}
                </SelectInputItem>
                <SelectInputItem value="credit_card" custom={{ root: 'text-nowrap' }}>
                  {t('paymentMethod.credit_card')}
                </SelectInputItem>
                <SelectInputItem value="pix" custom={{ root: 'text-nowrap' }}>
                  {t('paymentMethod.pix')}
                </SelectInputItem>
                <SelectInputItem value="transfer" custom={{ root: 'text-nowrap' }}>
                  {t('paymentMethod.transfer')}
                </SelectInputItem>
              </SelectInput>
              <SelectInput
                label={t('planForm.fields.expirationDay')}
                value={String(form.expirationDay)}
                onValueChange={v => updateField('expirationDay', Number(v))}
              >
                {dayOptions.map(day => (
                  <SelectInputItem key={day} value={String(day)} custom={{ root: 'text-nowrap' }}>
                    Dia {day}
                  </SelectInputItem>
                ))}
              </SelectInput>
            </div>

            {!isEditing && form.valueRegistration > 0 && (
              <Checkbox
                label="Gerar Parcela de Matrícula"
                checked={form.generateEnrollmentParcel}
                onCheckedChange={c => {
                  if (c !== 'indeterminate') {
                    updateField('generateEnrollmentParcel', c);
                  }
                }}
              />
            )}

            {/* ── Cálculo de parcelas ── */}
            <SectionTitle>Cálculo de Parcelas</SectionTitle>
            <div className="grid grid-cols-1 gap-md md:grid-cols-3">
              <TextField
                fullWidth
                size="md"
                label={t('planForm.fields.totalValue')}
                placeholder={t('planForm.fields.totalValue')}
                type="number"
                value={String(form.totalValue)}
                onChange={e => updateField('totalValue', Number(e.target.value))}
              />
              <TextField
                fullWidth
                size="md"
                label={t('planForm.fields.discount')}
                placeholder={t('planForm.fields.discount')}
                type="number"
                value={String(form.discount)}
                onChange={e => updateField('discount', Number(e.target.value))}
              />
              <TextField
                fullWidth
                size="md"
                label={t('planForm.fields.numberOfParcels')}
                placeholder={t('planForm.fields.numberOfParcels')}
                type="number"
                value={String(form.numberOfParcels)}
                onChange={e => updateField('numberOfParcels', Number(e.target.value))}
              />
            </div>

            {isEditing && (
              <Checkbox
                label="Recalcular Parcelas ao Salvar"
                checked={form.updatePlanParcels}
                onCheckedChange={c => {
                  if (c !== 'indeterminate') {
                    updateField('updatePlanParcels', c);
                  }
                }}
              />
            )}

            {/* ── Resumo calculado ── */}
            <div className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface p-xs">
              <div className="grid grid-cols-2 gap-xs sm:grid-cols-4">
                <CalcField label={t('planForm.fields.monthlyValue')} value={fmtBrl(monthlyValue)} />
                <CalcField
                  label={t('planForm.fields.totalWithDiscount')}
                  value={fmtBrl(totalWithDiscount)}
                />
                <CalcField
                  label="Total c/ Desc. Institucional"
                  value={fmtBrl(totalWithSchoolDiscount)}
                />
                <CalcField
                  label="Nº Parcelas"
                  value={`${form.numberOfParcels}x de ${fmtBrl(monthlyValue)}`}
                />
              </div>
            </div>
          </div>
        </TabMenuContent>
      </TabMenu>

      {/* ── Actions ── */}
      <div className="flex items-center justify-end gap-xs border-t-hairline border-ctx-layout-border pt-md">
        <Button size="lg" hierarchy="secondary" onClick={onCancel}>
          {t('actions.cancel')}
        </Button>
        <Button size="lg" type="submit">
          {t('actions.save')}
        </Button>
      </div>
    </form>
  );
}
